import { Routes } from '@angular/router';
import { NON_EXTERNAL_ROLES, RolesEnum } from '@definitions/role.interface';
import { PackageFeature } from '@definitions/subscription.interface';
import { authGuard } from '@guards/auth.guard';
import { roleGuard } from '@guards/company-type.guard';
import { maintenanceGuard } from '@guards/maintenance.guard';
import { packageGuard } from '@guards/package-access.guard';
import { pendingChangesGuard } from '@guards/pending-changes';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then((m) => m.LoginComponent)
  },
  {
    path: 'forget-password',
    loadComponent: () =>
      import('./pages/forget-password/forget-password.component').then((m) => m.ForgetPasswordComponent)
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent)
  },
  {
    path: 'invitation',
    loadComponent: () => import('./pages/invitation/invitation.component').then((m) => m.InvitationComponent)
  },
  {
    path: 'maintenance',
    canActivate: [maintenanceGuard],
    loadComponent: () => import('./pages/maintenance/maintenance.component').then((m) => m.MaintenanceComponent)
  },
  {
    path: 'guest-collaborator',
    loadComponent: () =>
      import('./pages/guest-collaborator/guest-collaborator.component').then((m) => m.GuestCollaboratorComponent)
  },
  {
    path: 'upgrade/:token/completed',
    data: { outcome: 'completed' },
    loadComponent: () =>
      import('./pages/upgrade-plan-result/upgrade-plan-result.component').then((m) => m.UpgradePlanResultComponent)
  },
  {
    path: 'upgrade/:token/failed',
    data: { outcome: 'failed' },
    loadComponent: () =>
      import('./pages/upgrade-plan-result/upgrade-plan-result.component').then((m) => m.UpgradePlanResultComponent)
  },
  {
    path: 'upgrade/:token',
    loadComponent: () => import('./pages/upgrade-plan/upgrade-plan.component').then((m) => m.UpgradePlanComponent)
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    children: [
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full'
      },
      {
        path: 'overview',
        canActivate: [roleGuard],
        data: {
          allowedRoles: NON_EXTERNAL_ROLES
        },
        loadComponent: () => import('./pages/overview/overview.component').then((m) => m.OverviewComponent)
      },
      {
        path: 'chat/:chatId',
        canActivate: [roleGuard],
        data: {
          allowedRoles: NON_EXTERNAL_ROLES
        },
        loadComponent: () => import('./pages/chat/chat.component').then((m) => m.ChatComponent)
      },
      {
        path: 'users',
        canActivate: [roleGuard],
        data: {
          allowedRoles: NON_EXTERNAL_ROLES
        },
        loadComponent: () => import('./pages/users/users.component').then((m) => m.UsersComponent)
      },
      {
        path: 'contacts',
        canActivate: [packageGuard],
        data: {
          requireFeature: PackageFeature.Invoicing
        },
        loadComponent: () => import('./pages/contacts/contacts.component').then((m) => m.ContactsComponent)
      },
      {
        path: 'clients',
        canActivate: [packageGuard],
        data: {
          requireFeature: PackageFeature.AgencyWorkspace
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/clients/clients.component').then((m) => m.ClientsComponent)
          },
          {
            path: ':company_id',
            loadComponent: () => import('./pages/clients/clients.component').then((m) => m.ClientsComponent)
          }
        ]
      },
      {
        path: 'user-usage-stats',
        canActivate: [roleGuard],
        data: {
          allowedRoles: [RolesEnum.SUPERADMIN]
        },
        loadComponent: () =>
          import('./pages/user-usage-stats/user-usage-stats.component').then((m) => m.UserUsageStatsComponent)
      },
      {
        path: 'agent/facturacion',
        canActivate: [packageGuard],
        data: {
          requireFeature: PackageFeature.Invoicing
        },
        loadComponent: () => import('./pages/invoicing/invoicing.component').then((m) => m.InvoicingComponent)
      },
      {
        path: 'agent/notificaciones-oficiales',
        canActivate: [packageGuard],
        data: {
          requireFeature: PackageFeature.AgencyWorkspace
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/aeat-notifications/aeat-notifications.component').then(
                (m) => m.AEATNotificationsComponent
              )
          },
          {
            path: ':notification_id',
            loadComponent: () =>
              import('./pages/aeat-notifications-details/aeat-notifications-details').then(
                (m) => m.AeatNotificationsDetails
              )
          }
        ]
      },
      {
        path: 'agent/regulatory-radar',
        canActivate: [packageGuard],
        data: {
          requireFeature: PackageFeature.AgencyWorkspace
        },
        loadComponent: () =>
          import('./pages/regulatory-radar/regulatory-radar.component').then((m) => m.RegulatoryRadarComponent)
      },
      {
        path: 'backoffice',
        canActivate: [roleGuard, packageGuard],
        data: {
          allowedRoles: [RolesEnum.ADMIN, RolesEnum.SUPERADMIN],
          requireFeature: PackageFeature.AgencyWorkspace
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/backoffice/dashboard/dashboard.component').then((m) => m.DashboardComponent)
          },
          /* TODO: Enable knowledge base route when the feature is ready.
          {
            path: 'knowledge-base',
            loadComponent: () =>
              import('./pages/backoffice/knowledge-base/knowledge-base.component').then((m) => m.KnowledgeBaseComponent)
          }*/ {
            path: 'new-clients',
            canActivate: [packageGuard],
            data: { requireFeature: PackageFeature.AgencyWorkspace },
            loadComponent: () =>
              import('./pages/backoffice/new-clients/new-clients.component').then((m) => m.NewClientsComponent)
          },
          {
            path: 'welcome_message',
            canActivate: [roleGuard, packageGuard],
            data: {
              allowedRoles: [RolesEnum.ADMIN, RolesEnum.SUPERADMIN],
              requireFeature: PackageFeature.AgencyWorkspace
            },
            loadComponent: () =>
              import('./pages/backoffice/welcome-message/welcome-message.component').then(
                (m) => m.WelcomeMessageComponent
              )
          },
          {
            path: 'channels',
            loadComponent: () =>
              import('./pages/backoffice/channels-config/channels-config.component').then(
                (m) => m.ChannelsConfigComponent
              )
          }
        ]
      },
      {
        path: 'communications',
        loadComponent: () =>
          import('./pages/backoffice/communications.component/communications.component').then(
            (m) => m.CommunicationsComponent
          )
      },
      {
        path: 'request-detail/:ticketId',
        loadComponent: () =>
          import('./pages/backoffice/ticket-detail/ticket-detail.component').then((m) => m.TicketDetailComponent)
      },
      {
        path: 'my-profile',
        canActivate: [roleGuard],
        data: {
          allowedRoles: NON_EXTERNAL_ROLES
        },
        loadComponent: () => import('./pages/my-profile/my-profile.component').then((m) => m.MyProfileComponent),
        canDeactivate: [pendingChangesGuard]
      },
      {
        path: 'companies-config',
        canActivate: [roleGuard, packageGuard],
        data: {
          allowedRoles: [RolesEnum.ADMIN, RolesEnum.SUPERADMIN],
          requireFeature: PackageFeature.Invoicing
        },
        loadComponent: () =>
          import('./pages/companies-config/companies-config.component').then((m) => m.CompaniesConfigComponent),
        canDeactivate: [pendingChangesGuard]
      },
      {
        path: 'files',
        canActivate: [roleGuard, packageGuard],
        data: {
          allowedRoles: NON_EXTERNAL_ROLES,
          excludedFeature: PackageFeature.AgencyWorkspace
        },
        loadComponent: () => import('./pages/shared-files/shared-files.component').then((m) => m.SharedFilesComponent)
      },
      {
        path: 'report',
        canActivate: [roleGuard, packageGuard],
        data: {
          allowedRoles: [RolesEnum.ADMIN, RolesEnum.SUPERADMIN],
          requireFeature: PackageFeature.AgencyWorkspace
        },
        loadComponent: () => import('./pages/report/report.component').then((m) => m.ReportComponent)
      }
    ]
  },
  {
    path: 'design-system',
    loadComponent: () => import('./pages/design-system/design-system.component').then((m) => m.DesignSystemComponent)
  }
];
